import axios from "src/utils/axios";
import { createSlice } from "@reduxjs/toolkit";

// ----------------------------------------------------------------------

const initialState = {
  isLoading: false,
  hasError: false,
  analytics: [],
  analyticFranquicia: [],
  printer: null,
  createdSuccess: false,
  updatedSuccess: false,
  deletedSuccess: false,

  intervals: [],
  // Falló la última petición de estadísticas del restaurante (analyticFranquicia).
  analyticFranquiciaError: false,

  rentabilidad: null,
  rentabilidadLoading: false,

  statsEmpleados: [],
  statsEmpleadosLoading: false,

  // ─── Cocina (Sprint 2 — tiempos KDS) ──────────────────────────
  // Payload de `GET /api/analytics/kitchen/times[/today]` con shape:
  //   { from, to, byProduct: [...], byDay: [...], totals: {...} }
  // Se guarda una sola referencia; al cambiar el rango se sobrescribe.
  kitchenTimes: null,
  kitchenTimesLoading: false,
  kitchenTimesToday: null,
  kitchenTimesTodayLoading: false,
  kitchenTimesSyncing: false,
};

const slice = createSlice({
  name: "analytics",
  initialState,
  reducers: {
    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    // HAS ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.hasError = true;
    },

    // GET PRODUCTS
    getAnalyticsSuccess(state, action) {
      state.isLoading = false;
      state.analytics = action.payload;
    },

    getAnalyticSuccess(state, action) {
      state.isLoading = false;
      state.analyticFranquiciaError = false;
      state.analyticFranquicia = action.payload;
    },

    startAnalyticFranquicia(state) {
      state.isLoading = true;
      state.analyticFranquiciaError = false;
    },

    // Sin datos del periodo pedido: no se deja pintado el periodo anterior.
    analyticFranquiciaError(state) {
      state.isLoading = false;
      state.hasError = true;
      state.analyticFranquiciaError = true;
      state.analyticFranquicia = [];
    },

    startRentabilidadLoading(state) {
      state.rentabilidadLoading = true;
    },

    getRentabilidadSuccess(state, action) {
      state.rentabilidadLoading = false;
      state.rentabilidad = action.payload;
    },

    rentabilidadError(state) {
      state.rentabilidadLoading = false;
    },

    startStatsEmpleadosLoading(state) {
      state.statsEmpleadosLoading = true;
    },
    getStatsEmpleadosSuccess(state, action) {
      state.statsEmpleadosLoading = false;
      state.statsEmpleados = action.payload;
    },
    statsEmpleadosError(state) {
      state.statsEmpleadosLoading = false;
    },

    // ─── Cocina — tiempos KDS ─────────────────────────────────────
    startKitchenTimesLoading(state) {
      state.kitchenTimesLoading = true;
    },
    getKitchenTimesSuccess(state, action) {
      state.kitchenTimesLoading = false;
      state.kitchenTimes = action.payload;
    },
    kitchenTimesError(state) {
      state.kitchenTimesLoading = false;
    },
    startKitchenTimesTodayLoading(state) {
      state.kitchenTimesTodayLoading = true;
    },
    getKitchenTimesTodaySuccess(state, action) {
      state.kitchenTimesTodayLoading = false;
      state.kitchenTimesToday = action.payload;
    },
    kitchenTimesTodayError(state) {
      state.kitchenTimesTodayLoading = false;
    },
    startKitchenTimesSync(state) {
      state.kitchenTimesSyncing = true;
    },
    endKitchenTimesSync(state) {
      state.kitchenTimesSyncing = false;
    },
  },
});

// Reducer
export default slice.reducer;

// Actions
export const { getAnalyticsSuccess } = slice.actions;

// ----------------------------------------------------------------------

export function getAnaliticsToday() {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    try {
      const response = await axios.get(`analytics/getstadisticastoday`);
      dispatch(slice.actions.getAnalyticsSuccess(response.data));
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getAnaliticsWeek() {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    try {
      const response = await axios.get(`analytics/getstadisticasweek`);
      dispatch(slice.actions.getAnalyticsSuccess(response.data));
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getAnaliticsMonth() {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    try {
      const response = await axios.get(`analytics/getstadisticasmonth`);
      dispatch(slice.actions.getAnalyticsSuccess(response.data));
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getAnaliticsYear() {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    try {
      const response = await axios.get(`analytics/getstadisticasyear`);
      dispatch(slice.actions.getAnalyticsSuccess(response.data));
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

// 2026-11-03 · Solo cuenta la última petición de estadísticas del
// restaurante. Al cambiar de periodo («Este mes» → «Mes anterior») la
// respuesta que llegaba tarde del periodo anterior se pintaba con la
// etiqueta del nuevo (dosmas: noviembre como «Mes anterior», −175 %).
let analyticFranquiciaSeq = 0;

function loadAnalyticFranquicia(request) {
  return async (dispatch) => {
    const seq = ++analyticFranquiciaSeq;
    dispatch(slice.actions.startAnalyticFranquicia());

    try {
      const response = await request();
      if (seq !== analyticFranquiciaSeq) return;
      dispatch(slice.actions.getAnalyticSuccess(response.data));
    } catch (error) {
      if (seq !== analyticFranquiciaSeq) return;
      if (process.env.NODE_ENV !== "production") console.error(error);
      dispatch(slice.actions.analyticFranquiciaError());
    }
  };
}

export function getAnaliticsFranquiciaToday(id) {
  return loadAnalyticFranquicia(() =>
    axios.get(`analytics/getstadisticasfranquiciatoday/${id}`),
  );
}

export function GetStadisticasFranquiciaDaysBack(daysBack) {
  return loadAnalyticFranquicia(() =>
    axios.get(`analytics/GetStadisticasFranquiciaDaysBack/${daysBack}`),
  );
}

export function getAnaliticsFranquiciaWeek(id) {
  return loadAnalyticFranquicia(() =>
    axios.get(`analytics/getstadisticasfranquiciaweek/${id}`),
  );
}

export function getAnaliticsFranquiciaLastWeek(id) {
  return loadAnalyticFranquicia(() =>
    axios.get(`analytics/getstadisticasfranquicialastweek/${id}`),
  );
}

export function getAnaliticsFranquiciaMonth(id) {
  return loadAnalyticFranquicia(() =>
    axios.get(`analytics/getstadisticasfranquiciamonth/${id}`),
  );
}

export function getAnaliticsFranquiciaLastMonth(id) {
  return loadAnalyticFranquicia(() =>
    axios.get(`analytics/getstadisticasfranquicialastmonth/${id}`),
  );
}

export function getAnaliticsFranquiciaYear(id) {
  return loadAnalyticFranquicia(() =>
    axios.get(`analytics/getstadisticasfranquiciayear/${id}`),
  );
}

export function getAnaliticsFranquiciaLastYear(id) {
  return loadAnalyticFranquicia(() =>
    axios.get(`analytics/getstadisticasfranquicialastyear/${id}`),
  );
}

export function getAnaliticsByIntervals(intervals) {
  return loadAnalyticFranquicia(() =>
    axios.post(`analytics/GetAnalytics`, { intervals }),
  );
}

export function getRentabilidad(desde, hasta) {
  return async (dispatch) => {
    dispatch(slice.actions.startRentabilidadLoading());
    try {
      const params = new URLSearchParams({ desde, hasta });
      const response = await axios.get(`analytics/GetRentabilidad?${params}`);
      dispatch(slice.actions.getRentabilidadSuccess(response.data));
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      dispatch(slice.actions.rentabilidadError());
    }
  };
}

export function getStatsEmpleados(desde, hasta) {
  return async (dispatch) => {
    dispatch(slice.actions.startStatsEmpleadosLoading());
    try {
      const params = new URLSearchParams({ desde, hasta });
      const response = await axios.get(`analytics/GetStatsEmpleados?${params}`);
      dispatch(slice.actions.getStatsEmpleadosSuccess(response.data));
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      dispatch(slice.actions.statsEmpleadosError());
    }
  };
}

// ─── Cocina — tiempos KDS (Sprint 2) ──────────────────────────────────
// Los endpoints los sirve el API principal (KitchenAnalyticsController).
// La agregación diaria la ejecuta el HostedService del TaskMicro, así que
// el rango histórico devuelve datos hasta AYER inclusive. Para HOY hay un
// endpoint aparte que calcula on-the-fly.

/**
 * Rango histórico agregado.
 * @param {{ from: string, to: string, idDisplay?: number|null }} params
 *        - from/to en formato ISO date (YYYY-MM-DD). Ambos inclusive.
 *        - idDisplay opcional para filtrar por display (barra/cocina).
 */
export function getKitchenTimes({ from, to, idDisplay = null } = {}) {
  return async (dispatch) => {
    dispatch(slice.actions.startKitchenTimesLoading());
    try {
      const params = new URLSearchParams({ from, to });
      if (idDisplay != null && idDisplay !== "") {
        params.append("idDisplay", String(idDisplay));
      }
      const response = await axios.get(`analytics/kitchen/times?${params}`);
      dispatch(slice.actions.getKitchenTimesSuccess(response.data));
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      dispatch(slice.actions.kitchenTimesError());
    }
  };
}

/** Hoy in-flight (query directo sobre KitchenTicket, sin depender del scheduler). */
export function getKitchenTimesToday({ idDisplay = null } = {}) {
  return async (dispatch) => {
    dispatch(slice.actions.startKitchenTimesTodayLoading());
    try {
      const params = new URLSearchParams();
      if (idDisplay != null && idDisplay !== "") {
        params.append("idDisplay", String(idDisplay));
      }
      const suffix = params.toString() ? `?${params}` : "";
      const response = await axios.get(`analytics/kitchen/times/today${suffix}`);
      dispatch(slice.actions.getKitchenTimesTodaySuccess(response.data));
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      dispatch(slice.actions.kitchenTimesTodayError());
    }
  };
}

/**
 * Fuerza la re-agregación del rango en el backend (para el restaurante
 * autenticado) y refresca la vista al terminar. Devuelve una promesa que
 * resuelve con `{ days }` o rechaza — el caller pinta el snackbar.
 *
 * @param {{ from: string, to: string, idDisplay?: number|null }} params
 */
export function syncKitchenTimes({ from, to, idDisplay = null } = {}) {
  return async (dispatch) => {
    dispatch(slice.actions.startKitchenTimesSync());
    try {
      const params = new URLSearchParams({ from, to });
      const response = await axios.post(`analytics/kitchen/sync?${params}`);
      // Al agregar puede haber cambiado ambos: rango y "hoy" (si hoy cae dentro).
      await Promise.all([
        dispatch(getKitchenTimes({ from, to, idDisplay })),
        dispatch(getKitchenTimesToday({ idDisplay })),
      ]);
      return response.data;
    } finally {
      dispatch(slice.actions.endKitchenTimesSync());
    }
  };
}
